import axios from 'axios';
import { getExternalFeedUrl } from 'helpers/feeds/generalHelpers';
import {
  getSiteDomain,
  getHash,
  getParsedJson,
  debugLogs,
  getS2Sdomain,
} from '../utils/common';
import { callWhenBrowserIsIdle } from './delayFnExecution';
import logger from './logger';

/** @typedef {import('axios').AxiosResponse<any>} AxiosResponseAny */

const serverAxiosService = (() => {
  if (typeof window !== 'undefined') {
    return null;
  }
  // eslint-disable-next-line import/no-commonjs, global-require
  const axiosConfigService = require('../server/services/axiosConfigService');
  axiosConfigService.startAxiosConfigService().catch(() => {});
  // eslint-disable-next-line import/no-commonjs, global-require
  return require('../server/services/axiosTransportService');
})();

class MakeRequestClass {
  static getTimeoutByUrl(url = '') {
    if (typeof window !== 'undefined' || !serverAxiosService) {
      return undefined;
    }
    return serverAxiosService.getAxiosPolicy(url).requestTimeout;
  }

  constructor(obj) {
    this.enableWebWorker =
      typeof window !== 'undefined' && typeof Worker === 'function';
    this.requestPromises = {};
    this.axiosInstance = axios.create({
      ...obj,
    });
    if (this.enableWebWorker) {
      this.initWebWorker();
    }
  }

  initWebWorker = () => {
    try {
      window.isRequestWorkerInitialized = false;
      this.worker = new Worker('/assets/request_worker.js');
      this.worker.onerror = event => {
        // this get calls after makerequest object is freeze, hence can't add in this.
        // eslint-disable-next-line no-console
        debugLogs('error webworker', event);
        window.isRequestWorkerInitialized = false;
      };
      // binding event
      this.worker.onmessage = event => {
        callWhenBrowserIsIdle(() => {
          const parsedJson = getParsedJson(event.data);
          const { eventName = '' } = parsedJson || {};
          if (eventName === 'request_worker_loaded') {
            window.isRequestWorkerInitialized = true;
          } else {
            this.onWorkerMessage(event);
          }
        });
      };
    } catch (err) {
      window.isRequestWorkerInitialized = false;
    }
  };

  static appendDomainToUrl(url, params) {
    let isRelativeUrl;
    const endpointData = [];

    const domain = getSiteDomain();

    if (url && typeof url === 'string') {
      isRelativeUrl = url.indexOf('http') === -1 && url.indexOf('.com') === -1;
    }
    if (isRelativeUrl) {
      if (params[2] !== 'skipfeedengine') {
        endpointData.push(getExternalFeedUrl());
      }
      if (url[0] === '/') {
        endpointData.push(domain);
      } else {
        endpointData.push(domain);
        endpointData.push('/');
      }
    }
    endpointData.push(url);
    if (
      typeof window === 'undefined' &&
      Array.isArray(endpointData) &&
      endpointData.length > 0
    ) {
      endpointData[0] = endpointData[0]
        .replace(domain, getS2Sdomain())
        .replace(
          'https://toifeeds.indiatimes.com/treact',
          'https://int-toifeeds.indiatimes.com',
        );
    }

    return endpointData.join('');
  }

  static handleError(error) {
    // This to protect logging on client side
    if (typeof window !== 'undefined') {
      return;
    }
    const config = error && error.config ? error.config : {};
    const method = (config.method || '').toUpperCase();
    const requestUrl = config.url;
    if (error.response) {
      logger.error('Axios response error', error, {
        event: 'axios_error',
        request_method: method,
        request_url: requestUrl,
        status_code: error.response.status,
      });
    } else if (error.request) {
      logger.error('Axios network error', error, {
        event: 'axios_error',
        request_method: method,
        request_url: requestUrl,
      });
    } else {
      logger.error('Axios request setup error', error, {
        event: 'axios_error',
        request_method: method,
        request_url: requestUrl,
      });
    }
  }

  onWorkerMessage = event => {
    const parsedJson = getParsedJson(event.data);
    const { status, webWorkerKey } = parsedJson;
    if (this.requestPromises[webWorkerKey]) {
      if (status >= 200 && status < 300) {
        this.requestPromises[webWorkerKey].resolve(parsedJson);
      } else {
        this.requestPromises[webWorkerKey].reject({
          response: parsedJson,
          request: parsedJson.request,
          config: parsedJson.config,
          message: `Getting Error while hitting ${webWorkerKey}`,
        });
      }
      delete this.requestPromises[webWorkerKey];
    }
  };

  getWebWorkerPromise(method, params) {
    let key;
    try {
      if (method === 'GET') {
        key = `${params[0]}_${Date.now()}`;
      } else {
        key = `${params[0]}_${getHash(params[1] || '')}`;
      }

      const pmObject = JSON.parse(
        JSON.stringify({
          method,
          params,
          webWorkerKey: key,
        }),
      );

      this.worker.postMessage(pmObject);
      return new Promise((resolve, reject) => {
        this.requestPromises[key] = {
          resolve,
          reject,
        };
      });
    } catch (err) {
      return new Promise((resolve, reject) => {
        reject();
      });
    }
  }
  removeCommas = str => str.replace(/,/g, ':');

  getURL = params => {
    let url = '';
    let urlParamString = '';
    if (params.length > 0) {
      url = params[0];
      const { params: urlParams } = params[1] || {};
      if (typeof urlParams === 'object') {
        urlParamString = Object.keys(urlParams)
          .map(key => `${key}=${urlParams[key]}`)
          .join('&');
      }
      const urlCombiner = url.indexOf('?') > -1 ? '&' : '?';
      url = urlParamString ? `${url}${urlCombiner}${urlParamString}` : url;
    }
    return url;
  };

  IsValiderRequestCallViaWorker = (params = []) => {
    let isWhiteList = false;
    const url = params[0] || '';
    const { skipWebWorker } = params[1] || {};
    if (skipWebWorker) {
      return false;
    }
    const whiteListToiFeedArray = [
      '/toifeeds.indiatimes.com/treact/',
      '/pwastg.timesofindia.com/feeds/',
    ];
    const whiteListXSLTArray = [
      '/timesofindia.indiatimes.com/',
      '/m.timesofindia.com/',
    ];
    const whiteListJarvisArray = ['/jarvis.indiatimes.com/'];
    const whiteListS3Feeds = ['/toibnews.timesofindia.indiatimes.com/'];

    const whiteListArray = [
      ...whiteListToiFeedArray,
      ...whiteListXSLTArray,
      ...whiteListJarvisArray,
      ...whiteListS3Feeds,
    ];
    for (let i = 0; i < whiteListArray.length; i += 1) {
      if (url.includes(whiteListArray[i])) {
        isWhiteList = true;
        break;
      }
    }

    return isWhiteList;
  };

  runBrowserGet(params) {
    return this.axiosInstance.get(...params).catch(err => {
      MakeRequestClass.handleError(err);
      return Promise.reject(err);
    });
  }

  runBrowserPost(params) {
    return this.axiosInstance.post(...params).catch(err => {
      MakeRequestClass.handleError(err);
      return Promise.reject(err);
    });
  }

  // eslint-disable-next-line class-methods-use-this
  runServerGet(url, axiosOptions, perfLabel) {
    return MakeRequestClass.withServerAxiosPerf('GET', url, perfLabel, () =>
      serverAxiosService.axiosServerGet(url, axiosOptions),
    );
  }

  // eslint-disable-next-line class-methods-use-this
  runServerPost(url, body, postConfig, perfLabel) {
    return MakeRequestClass.withServerAxiosPerf('POST', url, perfLabel, () =>
      serverAxiosService.axiosServerPost(url, body, postConfig),
    );
  }

  static withServerAxiosPerf(method, url, perfLabel, requestFn) {
    const startTime = Date.now();
    return requestFn()
      .then(response => {
        logger.perf(perfLabel, Date.now() - startTime, {
          event: 'axios_request',
          request_method: method,
          request_url: url,
        });
        return response;
      })
      .catch(err => {
        MakeRequestClass.handleError(err);
        return Promise.reject(err);
      });
  }

  /** @returns {Promise<AxiosResponseAny>} */
  get(...args) {
    const params = Object.assign([], args);
    params[0] = MakeRequestClass.appendDomainToUrl(params[0], params);
    //@todo can we use __PROD__ for this rather than params[0] ?
    if (
      params[0].includes('toifeeds') ||
      params[0].includes('pwastg') ||
      params[0].includes('localhost')
    ) {
      // if (global.upcache) {
      //   params[0] += '&upcache=2';
      // }
      //@todo from it's getting set ? do we have to check this in all the request ?
      const isMobileArgument = !!(
        params.length > 1 &&
        params[1] &&
        params[1].isMobile
      );

      if (isMobileArgument) {
        params[0] += '&ismobile=true';
      }
    }
    if (
      this.enableWebWorker &&
      window.isRequestWorkerInitialized &&
      this.IsValiderRequestCallViaWorker(params)
    ) {
      params[0] = this.getURL(params);
      // console.log(`pfTesting webworker get ${params[0]}`);
      // console.count(`pfTesting webworker get calls count`);
      return this.getWebWorkerPromise('GET', params).catch(err => {
        MakeRequestClass.handleError(err);
        return Promise.reject(err);
      });
    }
    // console.log(`pfTesting axios get ${params[0]}`);
    // console.count(`pfTesting axios get calls count`);
    if (typeof window === 'undefined' && serverAxiosService) {
      const requestUrl = params[0];
      return this.runServerGet(
        requestUrl,
        { ...(params[1] || {}) },
        'Axios GET completed',
      );
    }

    return this.runBrowserGet(params);
  }

  /** @returns {Promise<AxiosResponseAny>} */
  getwap(...args) {
    const params = Object.assign([], args);
    params[0] = MakeRequestClass.appendDomainToUrl(params[0], params);
    if (typeof window === 'undefined' && serverAxiosService) {
      const requestUrl = params[0];
      return this.runServerGet(
        requestUrl,
        { ...(params[1] || {}) },
        'Axios GETWAP completed',
      );
    }

    return this.runBrowserGet(params);
  }

  /** @returns {Promise<AxiosResponseAny>} */
  post(...args) {
    const params = Object.assign([], args);
    params[0] = MakeRequestClass.appendDomainToUrl(params[0]);
    const requestUrl = params[0];

    if (typeof window === 'undefined' && serverAxiosService) {
      return this.runServerPost(
        requestUrl,
        params[1],
        { ...(params[2] || {}) },
        'Axios POST completed',
      );
    }

    return this.runBrowserPost(params);
  }
}

const makeRequest = new MakeRequestClass();

Object.freeze(makeRequest);

export default makeRequest;
